import React, { useState, useEffect } from 'react';
import logo from '../TBL Logo White.png';
import './RateEachWine.css';

const RateEachWine = ({ classVersion, onNext, onBack }) => {
  // Initialize with saved data or defaults
  const getInitialRatings = () => {
    const savedRatings = sessionStorage.getItem('wineCharacteristicRatings');
    console.log('RateEachWine: Loading saved ratings:', savedRatings);
    if (savedRatings) {
      try {
        const parsedRatings = JSON.parse(savedRatings);
        console.log('RateEachWine: Parsed ratings:', parsedRatings);
        return parsedRatings;
      } catch (error) {
        console.error('RateEachWine: Error parsing saved ratings:', error);
      }
    }
    return {
      acidity: { wine1: 0, wine2: 0, wine3: 0 },
      sweetness: { wine1: 0, wine2: 0, wine3: 0 },
      body: { wine1: 0, wine2: 0, wine3: 0 }
    };
  };

  // Determine initial section based on completion status
  const getInitialSection = () => {
    const savedRatings = sessionStorage.getItem('wineCharacteristicRatings');
    if (savedRatings) {
      try {
        const ratings = JSON.parse(savedRatings);
        // Check if all wines have been rated for all characteristics
        if (ratings.acidity && ratings.sweetness && ratings.body) {
          const allRated = ['wine1', 'wine2', 'wine3'].every(wine => 
            ratings.acidity[wine] !== undefined && 
            ratings.sweetness[wine] !== undefined && 
            ratings.body[wine] !== undefined &&
            ratings.acidity[wine] > 0 && 
            ratings.sweetness[wine] > 0 && 
            ratings.body[wine] > 0
          );
          if (allRated) {
            return 'summary';
          }
        }
      } catch (error) {
        console.error('Error parsing saved ratings for initial section:', error);
      }
    }
    return 'intro'; // Default to intro for new users
  };

  const [currentSection, setCurrentSection] = useState(getInitialSection()); // 'summary', 'intro', 'how-to-taste', 'rate-wines'
  const [currentTasteStep, setCurrentTasteStep] = useState(0); // 0: Look, 1: Smell, 2: Taste
  const [currentWineIndex, setCurrentWineIndex] = useState(0); // 0: Wine 1, 1: Wine 2, 2: Wine 3
  const [wineRatings, setWineRatings] = useState(getInitialRatings);

  // Save ratings to session storage whenever they change
  useEffect(() => {
    console.log('RateEachWine: Saving ratings:', wineRatings);
    sessionStorage.setItem('wineCharacteristicRatings', JSON.stringify(wineRatings));
  }, [wineRatings]);

  const handleRatingChange = (characteristic, wine, rating) => {
    setWineRatings(prev => ({
      ...prev,
      [characteristic]: {
        ...prev[characteristic],
        [wine]: rating
      }
    }));
  };

  const tasteSteps = [
    {
      title: 'Look',
      description: 'Visually examine the wine to understand its characteristics',
      details: {
        acidity: classVersion === 'red' 
          ? 'The color red denotes a lower pH, while purple denotes a higher pH'
          : 'A pale straw color, or lack of color indicates a lower pH, whereas a more honey, golden or amber color indicates a higher pH',
        sweetness: 'The thinness of the legs indicates less sweetness v thicker legs which indicate more viscosity and sweetness is present',
        body: 'A light, more transparent concentration of color indicates lighter body v a darker more opaque concentration indicates full body'
      }
    },
    {
      title: 'Smell',
      description: 'Use your sense of smell to detect wine characteristics',
      details: {
        acidity: 'Smelling a sharp tingle indicates acidity, whereas a more neutral aroma is less acidic',
        sweetness: 'Smelling cooler aromas indicates less sweetness, whereas when the sensation of temperature or heat increases in a wine, that indicates more sweetness is present',
        body: 'Typically body is not something you investigate on the aroma'
      }
    },
    {
      title: 'Taste',
      description: 'Taste the wine to fully understand its characteristics',
      details: {
        acidity: 'Feel a sharpness on the tastebuds on your tongue v a flat or more neutral sensation',
        sweetness: 'Hold the wine on your tongue and check for viscosity - sweeter/riper wines will be more viscous or oily',
        body: 'As you swallow the wine investigate the texture. A full bodied wine\'s tastes and textures will linger on the palate for a long time, upwards of 30 seconds to a minute.'
      }
    }
  ];

  const wineSteps = [
    {
      id: 'wine1',
      name: 'Wine 1',
      description: 'Rate Wine 1 on all three characteristics'
    },
    {
      id: 'wine2', 
      name: 'Wine 2',
      description: 'Rate Wine 2 on all three characteristics'
    },
    {
      id: 'wine3',
      name: 'Wine 3', 
      description: 'Rate Wine 3 on all three characteristics'
    }
  ];

  const characteristics = [
    {
      id: 'acidity',
      title: 'Acidity',
      description: 'How much do you like the acidity level?',
      scale: '0 = I don\'t like it, 10 = I love it'
    },
    {
      id: 'sweetness',
      title: 'Sweetness', 
      description: 'How much do you like the sweetness level?',
      scale: '0 = I don\'t like it, 10 = I love it'
    },
    {
      id: 'body',
      title: 'Body',
      description: 'How much do you like the body level?',
      scale: '0 = I don\'t like it, 10 = I love it'
    }
  ];

  const wines = [
    { id: 'wine1', name: 'Wine 1' },
    { id: 'wine2', name: 'Wine 2' },
    { id: 'wine3', name: 'Wine 3' }
  ];

  const handleNextTasteStep = () => {
    if (currentTasteStep < tasteSteps.length - 1) {
      setCurrentTasteStep(currentTasteStep + 1);
    } else {
      setCurrentSection('rate-wines');
    }
    window.scrollTo(0, 0);
  };

  const handleNextWine = () => {
    if (currentWineIndex < wineSteps.length - 1) {
      setCurrentWineIndex(currentWineIndex + 1);
    } else {
      setCurrentSection('summary');
    }
    window.scrollTo(0, 0);
  };

  const handleBackTasteStep = () => {
    if (currentTasteStep > 0) {
      setCurrentTasteStep(currentTasteStep - 1);
    } else {
      setCurrentSection('intro');
    }
    window.scrollTo(0, 0);
  };

  const handleBackWine = () => {
    if (currentWineIndex > 0) {
      setCurrentWineIndex(currentWineIndex - 1);
    } else {
      setCurrentSection('how-to-taste');
    }
    window.scrollTo(0, 0);
  };

  // Navigation functions with scroll-to-top
  const navigateToIntro = () => {
    setCurrentSection('intro');
    window.scrollTo(0, 0);
  };

  const navigateToSummary = () => {
    setCurrentSection('summary');
    window.scrollTo(0, 0);
  };

  const navigateToHowToTaste = () => {
    setCurrentSection('how-to-taste');
    window.scrollTo(0, 0);
  };

  const navigateToRateWines = () => {
    setCurrentSection('rate-wines');
    window.scrollTo(0, 0);
  };

  // Calculate total score for each wine (out of 30)
  const calculateWineScore = (wineId) => {
    const acidity = wineRatings.acidity[wineId] || 0;
    const sweetness = wineRatings.sweetness[wineId] || 0;
    const body = wineRatings.body[wineId] || 0;
    return acidity + sweetness + body;
  };

  // Check if all wines have been rated for all characteristics
  const allWinesRated = () => {
    return wines.every(wine => {
      const acidity = wineRatings.acidity?.[wine.id] || 0;
      const sweetness = wineRatings.sweetness?.[wine.id] || 0;
      const body = wineRatings.body?.[wine.id] || 0;
      // All characteristics must be rated above 0
      return acidity > 0 && sweetness > 0 && body > 0;
    });
  };

  if (currentSection === 'summary') {
    return (
      <div className={`rate-each-wine ${classVersion}-theme`}>
        <div className="wine-header">
          <button className="back-button" onClick={onBack}>
            ← Back to Navigation
          </button>
          <img src={logo} className="wine-logo" alt="TBL Logo" />
          <h1>Wine Ratings Summary</h1>
          <p className="wine-description">
            Review your wine ratings and scores out of 30 points
          </p>
        </div>

        <div className="summary-container">
          {wines.map((wine) => {
            const totalScore = calculateWineScore(wine.id);
            const acidity = wineRatings.acidity[wine.id] || 0;
            const sweetness = wineRatings.sweetness[wine.id] || 0;
            const body = wineRatings.body[wine.id] || 0;
            
            return (
              <div key={wine.id} className="wine-summary-card">
                <div className="wine-summary-header">
                  <h2 className="wine-name">{wine.name}</h2>
                  <div className="total-score">
                    <span className="score-number">{totalScore.toFixed(1)}</span>
                    <span className="score-label">/ 30</span>
                  </div>
                </div>
                
                <div className="score-breakdown">
                  <div className="characteristic-score">
                    <span className="characteristic-label">Acidity:</span>
                    <span className="characteristic-value">{acidity.toFixed(1)}</span>
                  </div>
                  <div className="characteristic-score">
                    <span className="characteristic-label">Sweetness:</span>
                    <span className="characteristic-value">{sweetness.toFixed(1)}</span>
                  </div>
                  <div className="characteristic-score">
                    <span className="characteristic-label">Body:</span>
                    <span className="characteristic-value">{body.toFixed(1)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="summary-actions">
          <button 
            className="edit-button" 
            onClick={navigateToIntro}
          >
            Edit Ratings
          </button>
          {allWinesRated() && (
            <button className="next-button" onClick={onNext}>
              Continue to Next Section
            </button>
          )}
        </div>

        <div className="footer">
          <div className="footer-separator"></div>
          <img src={logo} className="footer-logo" alt="TBL Logo" />
        </div>
      </div>
    );
  }

  if (currentSection === 'intro') {
    return (
      <div className={`rate-each-wine ${classVersion}-theme`}>
        <div className="wine-header">
          <button className="back-button" onClick={onBack}>
            ← Back to Navigation
          </button>
          <img src={logo} className="wine-logo" alt="TBL Logo" />
          <h1>Rate Each Wine</h1>
          <p className="wine-description">
            Learn how to taste wine properly and then rate each wine based on your preferences
          </p>
        </div>

        <div className="intro-options">
          {allWinesRated() && (
            <div className="intro-card" onClick={navigateToSummary}>
              <div className="intro-icon">📊</div>
              <h3>View Summary</h3>
              <p>Review your wine ratings and scores</p>
            </div>
          )}
          
          <div className="intro-card" onClick={navigateToHowToTaste}>
            <div className="intro-icon">👃</div>
            <h3>How to Taste Wine</h3>
            <p>Learn the proper technique for tasting and evaluating wine</p>
          </div>
          
          <div className="intro-card" onClick={navigateToRateWines}>
            <div className="intro-icon">⭐</div>
            <h3>Rate Each Wine</h3>
            <p>Rate each wine based on acidity, sweetness, and body</p>
          </div>
        </div>

        <div className="footer">
          <div className="footer-separator"></div>
          <img src={logo} className="footer-logo" alt="TBL Logo" />
        </div>
      </div>
    );
  }

  if (currentSection === 'how-to-taste') {
    const currentStep = tasteSteps[currentTasteStep];
    
    return (
      <div className={`rate-each-wine ${classVersion}-theme`}>
        <div className="wine-header">
          <button className="back-button" onClick={handleBackTasteStep}>
            ← Back
          </button>
          <img src={logo} className="wine-logo" alt="TBL Logo" />
          <h1>How to Taste Wine</h1>
          <p className="wine-description">
            Step {currentTasteStep + 1} of {tasteSteps.length}: {currentStep.title}
          </p>
        </div>

        <div className="taste-step-container">
          <div className="taste-step-card">
            <h2 className="step-title">{currentStep.title}</h2>
            <p className="step-description">{currentStep.description}</p>
            
            <div className="characteristics-details">
              <div className="characteristic-detail">
                <h4>Acidity</h4>
                <p>{currentStep.details.acidity}</p>
              </div>
              <div className="characteristic-detail">
                <h4>Sweetness</h4>
                <p>{currentStep.details.sweetness}</p>
              </div>
              <div className="characteristic-detail">
                <h4>Body</h4>
                <p>{currentStep.details.body}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="next-button" onClick={handleNextTasteStep}>
            {currentTasteStep < tasteSteps.length - 1 ? 'Next Step' : 'Start Rating Wines'}
          </button>
        </div>

        <div className="footer">
          <div className="footer-separator"></div>
          <img src={logo} className="footer-logo" alt="TBL Logo" />
        </div>
      </div>
    );
  }

  if (currentSection === 'rate-wines') {
    const currentWine = wineSteps[currentWineIndex];
    
    return (
      <div className={`rate-each-wine ${classVersion}-theme`}>
        <div className="wine-header">
          <button className="back-button" onClick={handleBackWine}>
            ← Back
          </button>
          <img src={logo} className="wine-logo" alt="TBL Logo" />
          <h1>Rate Each Wine</h1>
          <p className="wine-description">
            {currentWine.name}: {currentWine.description}
          </p>
          <p className="rating-scale">Rate each characteristic from 0-10 (0 = I don't like it, 10 = I love it)</p>
        </div>

        <div className="wine-rating-container">
          <div className="wine-rating-card">
            <h2 className="wine-name">{currentWine.name}</h2>
            <p className="wine-instruction">Focus on this wine and rate each characteristic:</p>
            
            {characteristics.map((characteristic) => (
              <div key={characteristic.id} className="characteristic-rating">
                <h3 className="characteristic-title">{characteristic.title}</h3>
                <p className="characteristic-description">{characteristic.description}</p>
                <div className="rating-container">
                  <div className="slider-container">
                    <input
                      type="range"
                      min="0"
                      max="10"
                      step="0.1"
                      value={wineRatings[characteristic.id][currentWine.id]}
                      onChange={(e) => handleRatingChange(characteristic.id, currentWine.id, parseFloat(e.target.value))}
                      className="rating-slider"
                    />
                    <div className="slider-track"></div>
                    <div className="slider-ticks">
                      {[0, 2, 4, 6, 8, 10].map((tick) => {
                        // Browser formula: (value - min) / (max - min) * (trackWidth - thumbWidth) + thumbWidth/2
                        // For 0-10 range: (tick - 0) / (10 - 0) * (100% - thumbWidth) + thumbWidth/2
                        // Desktop: (tick / 10) * (100% - 28px) + 14px
                        // Mobile: (tick / 10) * (100% - 32px) + 16px
                        const desktopPosition = `calc(${(tick / 10) * 100}% - ${(tick / 10) * 28}px + 14px)`;
                        const mobilePosition = `calc(${(tick / 10) * 100}% - ${(tick / 10) * 32}px + 16px)`;
                        
                        return (
                          <div key={tick} className="slider-tick" style={{ 
                            left: desktopPosition,
                            '--mobile-position': mobilePosition
                          }}>
                            <div className="slider-tick-label">{tick}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="rating-value">
                    <span className="current-rating">{wineRatings[characteristic.id][currentWine.id].toFixed(1)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="action-buttons">
          <button className="next-button" onClick={handleNextWine}>
            {currentWineIndex < wineSteps.length - 1 ? `Rate ${wineSteps[currentWineIndex + 1].name}` : 'Continue to Next Section'}
          </button>
        </div>

        <div className="footer">
          <div className="footer-separator"></div>
          <img src={logo} className="footer-logo" alt="TBL Logo" />
        </div>
      </div>
    );
  }

  return null;
};

export default RateEachWine;
